import {
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  CreateNewFolder as CreateNewFolderIcon,
  Delete as DeleteIcon,
  Description as FileIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  FolderOutlined as FolderIcon,
  FolderOpenOutlined as FolderOpenIcon,
  MoreVert as MoreVertIcon,
  NoteAdd as NoteAddIcon,
  Refresh as RefreshIcon,
  UploadFileOutlined as UploadFileIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { ProjectFile, ProjectFileTreeNode, WorkspaceProject } from "../../../types";
import { hostname } from "../../../utils";
import { ALLOWED_DOCUMENT_EXTENSIONS, MAX_DOCUMENT_SIZE_MB } from "../../../constants";

interface ProjectFilesDialogProps {
  open: boolean;
  onClose: () => void;
  project: WorkspaceProject | null;
  companyId: string;
  getToken: () => Promise<string>;
  onFileSelect?: (file: ProjectFile) => void;
  onDetach?: () => void;
  eventListener?: (event: { type: string; error?: string; message?: string }) => void;
}

interface TreeItemProps {
  node: ProjectFileTreeNode;
  level: number;
  projectId: string;
  companyId: string;
  getToken: () => Promise<string>;
  onRefresh: () => void;
  onFileSelect?: (file: ProjectFile) => void;
  expandedFolders: Set<string>;
  toggleFolder: (path: string) => void;
}

const getFileIcon = (mimeType?: string) => {
  if (!mimeType) return <FileIcon fontSize="small" />;

  if (mimeType.startsWith("image/")) {
    return <FileIcon fontSize="small" color="primary" />;
  }
  if (mimeType.includes("javascript") || mimeType.includes("typescript")) {
    return <FileIcon fontSize="small" sx={{ color: "#f0db4f" }} />;
  }
  if (mimeType.includes("python")) {
    return <FileIcon fontSize="small" sx={{ color: "#3776ab" }} />;
  }
  if (mimeType.includes("html")) {
    return <FileIcon fontSize="small" sx={{ color: "#e34c26" }} />;
  }
  if (mimeType.includes("css")) {
    return <FileIcon fontSize="small" sx={{ color: "#264de4" }} />;
  }
  if (mimeType.includes("json")) {
    return <FileIcon fontSize="small" sx={{ color: "#5a9fd4" }} />;
  }
  return <FileIcon fontSize="small" />;
};

function TreeItem({
  node,
  level,
  projectId,
  companyId,
  getToken,
  onRefresh,
  onFileSelect,
  expandedFolders,
  toggleFolder,
}: TreeItemProps) {
  const isFolder = node.type === "folder";
  const isExpanded = expandedFolders.has(node.path);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const handleClick = () => {
    if (isFolder) {
      toggleFolder(node.path);
    } else if (onFileSelect) {
      handleFileSelect();
    }
  };

  const handleFileSelect = async () => {
    try {
      const token = await getToken();
      // Get the directory path (parent of the file)
      const dirPath = node.path.split("/").slice(0, -1).join("/") || "/";
      const response = await axios.get(
        `${hostname}/projects/${projectId}/files`,
        {
          params: { companyId, path: dirPath, includeContent: true },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const files = response.data || [];
      const file = files.find((f: ProjectFile) => f.name === node.name);
      if (file && onFileSelect) {
        onFileSelect(file);
      }
    } catch (error) {
      console.error("Error fetching file:", error);
    }
  };

  const handleDelete = async () => {
    setMenuAnchor(null);
    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/projects/${projectId}/files`,
        {
          params: { companyId, path: node.path.split("/").slice(0, -1).join("/") || "/" },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const files = response.data || [];
      const file = files.find((f: ProjectFile) => f.name === node.name);
      if (file) {
        await axios.delete(
          `${hostname}/projects/${projectId}/files/${file.id}`,
          {
            params: { companyId },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        onRefresh();
      }
    } catch (error) {
      console.error("Error deleting file:", error);
    }
  };

  return (
    <>
      <ListItemButton
        onClick={handleClick}
        sx={{
          pl: 2 + level * 2,
          py: 0.5,
          borderRadius: 1,
          "&:hover .actions": { opacity: 1 },
        }}
      >
        {isFolder && (
          <ListItemIcon sx={{ minWidth: 24 }}>
            {isExpanded ? (
              <ExpandMoreIcon fontSize="small" />
            ) : (
              <ChevronRightIcon fontSize="small" />
            )}
          </ListItemIcon>
        )}
        <ListItemIcon sx={{ minWidth: 32 }}>
          {isFolder ? (
            isExpanded ? (
              <FolderOpenIcon fontSize="small" color="primary" />
            ) : (
              <FolderIcon fontSize="small" color="primary" />
            )
          ) : (
            getFileIcon(node.mimeType)
          )}
        </ListItemIcon>
        <ListItemText
          primary={node.name}
          primaryTypographyProps={{
            variant: "body2",
            noWrap: true,
          }}
        />
        <Box
          className="actions"
          sx={{ opacity: 0, transition: "opacity 0.2s" }}
        >
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchor(e.currentTarget);
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
      </ListItemButton>

      {isFolder && isExpanded && node.children && (
        <Collapse in={isExpanded}>
          <List disablePadding>
            {node.children.map((child) => (
              <TreeItem
                key={child.path}
                node={child}
                level={level + 1}
                projectId={projectId}
                companyId={companyId}
                getToken={getToken}
                onRefresh={onRefresh}
                onFileSelect={onFileSelect}
                expandedFolders={expandedFolders}
                toggleFolder={toggleFolder}
              />
            ))}
          </List>
        </Collapse>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        {!isFolder && (
          <MenuItem onClick={() => { setMenuAnchor(null); handleFileSelect(); }}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            View/Edit
          </MenuItem>
        )}
        <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </>
  );
}

export default function ProjectFilesDialog({
  open,
  onClose,
  project,
  companyId,
  getToken,
  onFileSelect,
  onDetach,
  eventListener,
}: ProjectFilesDialogProps) {
  const theme = useTheme();
  const [tree, setTree] = useState<ProjectFileTreeNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    new Set(["/"])
  );
  const [creating, setCreating] = useState<"file" | "folder" | null>(null);
  const [newName, setNewName] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const fetchTree = useCallback(async () => {
    if (!project) return;
    setLoading(true);
    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/projects/${project.id}/tree`,
        {
          params: { companyId },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setTree(response.data || []);
    } catch (error) {
      console.error("Error fetching project tree:", error);
    } finally {
      setLoading(false);
    }
  }, [project, companyId, getToken]);

  useEffect(() => {
    if (open && project) {
      fetchTree();
    }
  }, [open, project, fetchTree]);

  const toggleFolder = (path: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  const handleCreate = async () => {
    if (!newName.trim() || !project) return;

    try {
      const token = await getToken();
      const path = `/${newName.trim()}`;

      if (creating === "folder") {
        await axios.post(
          `${hostname}/projects/${project.id}/files/mkdir`,
          { path, companyId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${hostname}/projects/${project.id}/files`,
          { path, companyId, content: "", isDirectory: false },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      setNewName("");
      setCreating(null);
      fetchTree();
    } catch (error) {
      console.error("Error creating:", error);
    }
  };

  // Text-based file extensions that can be read directly (no conversion needed)
  const TEXT_EXTENSIONS = [
    // Code files
    ".js", ".jsx", ".ts", ".tsx", ".py", ".rb", ".php", ".java", ".c", ".cpp", ".h", ".hpp",
    ".cs", ".go", ".rs", ".swift", ".kt", ".scala", ".r", ".m", ".mm",
    // Web files
    ".css", ".scss", ".sass", ".less", ".vue", ".svelte",
    // Data/config files
    ".xml", ".yaml", ".yml", ".toml", ".ini", ".env", ".conf", ".config",
    // Documentation
    ".markdown", ".rst", ".tex",
    // Shell/scripts
    ".sh", ".bash", ".zsh", ".fish", ".ps1", ".bat", ".cmd",
    // Other text files
    ".sql", ".graphql", ".gql", ".prisma", ".dockerfile", ".gitignore", ".editorconfig",
    ".eslintrc", ".prettierrc", ".babelrc", ".npmrc", ".nvmrc",
  ];

  // Files without extension that are commonly text
  const COMMON_TEXT_FILES = ["makefile", "dockerfile", "gemfile", "rakefile", "procfile", "readme", "license", "changelog"];

  // Document types that need conversion via /documents/convert (PDF, DOCX, XLSX, etc.)
  // These are handled by ALLOWED_DOCUMENT_EXTENSIONS from constants
  const CONVERTIBLE_EXTENSIONS = ALLOWED_DOCUMENT_EXTENSIONS.filter(
    ext => ![".txt", ".md", ".json", ".html"].includes(ext) // These are already in TEXT_EXTENSIONS or handled separately
  );

  const getFileExtension = (filename: string): string => {
    const lowerName = filename.toLowerCase();
    const lastDot = lowerName.lastIndexOf(".");
    return lastDot >= 0 ? lowerName.substring(lastDot) : "";
  };

  const isTextFile = (filename: string): boolean => {
    const ext = getFileExtension(filename);
    if (TEXT_EXTENSIONS.includes(ext) || [".txt", ".md", ".json", ".html"].includes(ext)) {
      return true;
    }
    const baseName = filename.toLowerCase().split("/").pop() || "";
    return COMMON_TEXT_FILES.includes(baseName);
  };

  const isConvertibleFile = (filename: string): boolean => {
    const ext = getFileExtension(filename);
    return CONVERTIBLE_EXTENSIONS.includes(ext as typeof CONVERTIBLE_EXTENSIONS[number]);
  };

  const isFileSupported = (filename: string): boolean => {
    return isTextFile(filename) || isConvertibleFile(filename);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !project) return;

    // Filter to only supported files
    const supportedFiles = Array.from(files).filter(f => isFileSupported(f.name));
    const skippedFiles = Array.from(files).filter(f => !isFileSupported(f.name));

    if (supportedFiles.length === 0) {
      eventListener?.({
        type: "SET_ERROR",
        error: "No supported files selected. Supported formats: code files, text, PDF, Word, Excel, CSV, and more.",
      });
      return;
    }

    // Check file sizes
    const oversizedFiles = supportedFiles.filter(f => f.size > MAX_DOCUMENT_SIZE_MB * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      eventListener?.({
        type: "SET_ERROR",
        error: `${oversizedFiles.length} file(s) exceed the ${MAX_DOCUMENT_SIZE_MB}MB limit and will be skipped.`,
      });
    }

    const validFiles = supportedFiles.filter(f => f.size <= MAX_DOCUMENT_SIZE_MB * 1024 * 1024);
    if (validFiles.length === 0) {
      return;
    }

    if (skippedFiles.length > 0) {
      console.log(`Skipped unsupported files: ${skippedFiles.map(f => f.name).join(", ")}`);
    }

    setUploading(true);
    try {
      const token = await getToken();

      // Separate text files from files that need conversion
      const textFiles = validFiles.filter(f => isTextFile(f.name));
      const convertibleFiles = validFiles.filter(f => isConvertibleFile(f.name));

      // Upload text files directly
      for (const file of textFiles) {
        const content = await readFileAsText(file);
        const path = `/${file.name}`;

        await axios.post(
          `${hostname}/projects/${project.id}/files`,
          {
            path,
            companyId,
            content,
            mimeType: file.type || "text/plain",
            isDirectory: false,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      // Convert and upload document files (PDF, DOCX, XLSX, etc.)
      if (convertibleFiles.length > 0) {
        const formData = new FormData();
        formData.append("companyId", companyId);
        for (const file of convertibleFiles) {
          formData.append("files", file);
        }

        const convertResponse = await axios.post(
          `${hostname}/documents/convert?companyId=${companyId}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        const convertedDocs = convertResponse.data.documents || [];
        for (const doc of convertedDocs) {
          const originalName = doc.name || "document";
          const path = `/${originalName}`;

          await axios.post(
            `${hostname}/projects/${project.id}/files`,
            {
              path,
              companyId,
              content: doc.content,
              mimeType: "text/plain",
              isDirectory: false,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
      }

      fetchTree();
    } catch (error: any) {
      console.error("Error uploading files:", error);
      const errorMsg = error.response?.data?.error || error.response?.data?.msg || "Failed to upload files";
      eventListener?.({
        type: "SET_ERROR",
        error: errorMsg,
      });
    } finally {
      setUploading(false);
      // Reset input so the same file can be uploaded again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const readFileAsText = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsText(file);
    });
  };

  if (!project) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FolderIcon color="primary" />
            <Typography variant="h6">{project.name}</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={fetchTree}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {/* Actions */}
        <Box
          sx={{
            mb: 2,
            display: "flex",
            gap: 1,
          }}
        >
          <Tooltip title="New File">
            <IconButton
              size="small"
              onClick={() => setCreating("file")}
              color={creating === "file" ? "primary" : "default"}
            >
              <NoteAddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="New Folder">
            <IconButton
              size="small"
              onClick={() => setCreating("folder")}
              color={creating === "folder" ? "primary" : "default"}
            >
              <CreateNewFolderIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Upload Files">
            <IconButton
              size="small"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? (
                <CircularProgress size={18} />
              ) : (
                <UploadFileIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".js,.jsx,.ts,.tsx,.py,.rb,.php,.java,.c,.cpp,.h,.hpp,.cs,.go,.rs,.swift,.kt,.scala,.r,.m,.mm,.html,.htm,.css,.scss,.sass,.less,.vue,.svelte,.json,.xml,.yaml,.yml,.toml,.ini,.env,.conf,.config,.md,.markdown,.txt,.rst,.tex,.sh,.bash,.zsh,.fish,.ps1,.bat,.cmd,.sql,.graphql,.gql,.prisma,.dockerfile,.gitignore,.editorconfig,.eslintrc,.prettierrc,.babelrc,.npmrc,.nvmrc,.pdf,.doc,.docx,.xls,.xlsx,.csv,.m4a,.mp3,.webm,.mp4,text/*"
            style={{ display: "none" }}
            onChange={handleFileUpload}
          />
          <Box sx={{ flex: 1 }} />
          {onDetach && (
            <Button size="small" color="error" onClick={onDetach}>
              Detach Project
            </Button>
          )}
        </Box>

        {/* New file/folder input */}
        {creating && (
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={creating === "file" ? "filename.txt" : "folder-name"}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCreate();
                if (e.key === "Escape") {
                  setCreating(null);
                  setNewName("");
                }
              }}
              autoFocus
              InputProps={{
                endAdornment: (
                  <Button size="small" onClick={handleCreate} disabled={!newName.trim()}>
                    Create
                  </Button>
                ),
              }}
            />
          </Box>
        )}

        {/* File Tree */}
        <Box sx={{ minHeight: 200, maxHeight: 400, overflow: "auto" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : tree.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography color="text.secondary" variant="body2" gutterBottom>
                No files yet
              </Typography>
              <Typography color="text.secondary" variant="caption">
                Create files or let the AI build your project
              </Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {tree.map((node) => (
                <TreeItem
                  key={node.path}
                  node={node}
                  level={0}
                  projectId={project.id}
                  companyId={companyId}
                  getToken={getToken}
                  onRefresh={fetchTree}
                  onFileSelect={onFileSelect}
                  expandedFolders={expandedFolders}
                  toggleFolder={toggleFolder}
                />
              ))}
            </List>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
